import { startUnifyInventoryConnection } from '@services/onboarding';
import myShopClient from '@services/nearlive';
import { CONNECTION_TYPE, CONNECTION_KIND, SUBMIT_TYPE } from '../constants';
import { extractSessionId, transformFormFields } from '../utils/shopUtils';

/**
 * Starts a new inventory connection (stock or product) for Unify onboarding
 *
 * @param {Object} options
 * @param {string} options.shopId - Shop ID
 * @param {string} options.provider - Provider identifier
 * @param {string} options.kind - Connection kind (stock/product)
 * @param {string} options.redirectUrl - OAuth redirect URL
 * @returns {Promise<Object>} Response with sessionId and finishSetupLink
 */
export const initiateConnection = async ({ shopId, provider, kind, redirectUrl, params }) => {
	return await startUnifyInventoryConnection({
		shopId,
		provider,
		redirectUrl,
		kind,
		params
	});
};

/**
 * Handles OAuth connection flow
 * Stores session ID and redirects to OAuth provider
 *
 * @param {Object} connection - Connection instance (stock or product)
 * @param {Object} response - API response with finishSetupLink
 * @param {string} sessionId - Extracted session ID
 */
export const handleOAuthFlow = (connection, response, sessionId) => {
	if (!response.finishSetupLink) {
		throw new Error('No OAuth URL received from server');
	}

	connection.updateSessionId(sessionId);
	window.location.href = response.finishSetupLink;
};

/**
 * Handles plug-and-play connection flow
 * For spinner type: stores session and starts polling
 * For manual type: stores session and waits for user to fill form
 *
 * @param {Object} connection - Connection instance
 * @param {Object} provider - Provider configuration
 * @param {string} sessionId - Session ID
 */
export const handlePlugAndPlayFlow = (connection, provider, sessionId) => {
	connection.updateSessionId(sessionId);

	// If spinner type, start polling immediately
	if (provider.connect?.submitType === SUBMIT_TYPE.SPINNER) {
		connection.setPollingForConnection(true);
	}
	// Otherwise, user fills form and submits manually
};

/**
 * Submits plug-and-play form data
 *
 * @param {Object} options
 * @param {string} options.sessionId - Session ID
 * @param {Object} options.formData - Form field values
 * @param {Object} options.provider - Provider configuration
 * @param {string} options.shopId - Shop ID
 * @returns {Promise<void>}
 */
export const submitPlugAndPlayForm = async ({ sessionId, formData, provider, shopId }) => {
	const client = await myShopClient();
	const transformedFields = transformFormFields(formData, provider, shopId);

	await client.post(`/api/onboarding/connect-sessions/${sessionId}`, { fields: transformedFields });
};

/**
 * Maps POS outlets to shops for a multi-outlet connect session (NEA-5988).
 * Writes the chosen locationId (and copies credentials) to each mapped shop's
 * inventory sources, then triggers their first stock download.
 *
 * @param {string} sessionId - Connect session ID
 * @param {Object} locationsMap - { [shopId]: locationId }
 * @returns {Promise<Object>} Response body
 */
export const mapConnectSessionLocations = async (sessionId, locationsMap) => {
	const client = await myShopClient();
	const { data } = await client.post(`/api/onboarding/connect-sessions/${sessionId}/map-locations`, { locationsMap });
	return data;
};

/**
 * Asks the backend whether this shop can pick its outlet using the org's
 * existing multi-outlet POS credentials — no re-authorisation (NEA-5988).
 *
 * @param {string} shopId - Shop to map
 * @returns {Promise<Object>} {available, sessionId?, provider?, providerLabel?, targetShopId?, locations?, needsReauth?}
 */
export const requestOutletFastPath = async (shopId) => {
	const client = await myShopClient();
	const { data } = await client.post(`/api/onboarding/outlet-fast-path/${shopId}`, {});
	return data;
};

/**
 * Gets redirect URL for OAuth/connection flows.
 *
 * Shop-scoped via the current pathname (/:shopId/onboarding on chains) — a flat
 * /onboarding return resolves currentShop to an arbitrary org sibling, which
 * would render the outlet picker against the wrong shop (NEA-5988; same fix
 * shape as NEA-5838's GBP redirect).
 *
 * @returns {string} Redirect URL
 */
export const getRedirectUrl = () => {
	return `${window.location.origin}${window.location.pathname}?pos_callback=true`;
};

/**
 * Handles connection errors with appropriate messaging
 *
 * @param {Error} err - Error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (err) => {
	const errorMessage = err.response?.data?.error?.message || err.message || '';

	if (
		err.response?.status === 500 &&
		(errorMessage.includes('resource not found') || errorMessage.includes('ResourceNotFoundException'))
	) {
		return 'Local development environment issue: inventory-sources table not accessible. Try testing against staging or set up local DynamoDB tables.';
	}

	return err.response?.data?.error?.message || err.response?.data?.message || 'Failed to start POS connection. Please try again.';
};
